/**
 * Composant Combobox avec recherche et multi-sélection
 */

import { LitElement, html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import comboboxStyles from "../styles/combobox.css?inline";
import { WidgetLanguage } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";

export interface ComboboxOption {
  id: string;
  label: string;
  count?: number;
}

@customElement("apidae-combobox")
export class Combobox extends LitElement {
  static styles = unsafeCSS(comboboxStyles);

  @property({ type: Array })
  options: ComboboxOption[] = [];

  @property({ type: Array })
  selectedValues: string[] = [];

  @property({ type: String })
  language: WidgetLanguage = WidgetLanguage.FR;

  @state()
  private searchQuery: string = "";

  @state()
  private isOpen: boolean = false;

  private get filteredOptions(): ComboboxOption[] {
    if (!this.searchQuery.trim()) {
      return this.options;
    }
    const query = this.searchQuery.toLowerCase();
    return this.options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }

  private handleSearchInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this.searchQuery = target.value;
    // Ouvrir automatiquement le dropdown quand on tape dans l'input
    if (!this.isOpen && this.searchQuery.trim()) {
      this.isOpen = true;
    }
  }

  private handleClearSearch() {
    this.searchQuery = "";
    this.isOpen = false;
  }

  private handleInputFocus() {
    // Ouvrir le dropdown quand on focus l'input
    if (this.filteredOptions.length > 0) {
      this.isOpen = true;
    }
  }

  private handleInputBlur() {
    // Ne pas fermer immédiatement pour permettre les clics sur les options
    // On fermera après un court délai
    setTimeout(() => {
      this.isOpen = false;
    }, 200);
  }

  private toggleOption(optionId: string) {
    const newSelected = [...this.selectedValues];
    const index = newSelected.indexOf(optionId);

    if (index > -1) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push(optionId);
    }

    this.selectedValues = newSelected;
    this.emitChange();
  }

  private emitChange() {
    this.dispatchEvent(
      new CustomEvent("selection-change", {
        detail: { selectedValues: this.selectedValues },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleToggleOpen() {
    this.isOpen = !this.isOpen;
  }

  private handleClickOutside(e: MouseEvent) {
    if (!this.contains(e.target as Node)) {
      this.isOpen = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleClickOutside);
  }

  render() {
    const selectedCount = this.selectedValues.length;
    const communePlaceholder = TranslationService.translate(
      "communePlaceholder",
      this.language,
    );
    const selectedText = TranslationService.translate(
      selectedCount > 1 ? "selectedPlural" : "selected",
      this.language,
    );
    const displayText =
      selectedCount > 0
        ? `${selectedCount} ${selectedText}`
        : communePlaceholder;
    const clearSearch = TranslationService.translate(
      "clearSearch",
      this.language,
    );
    const noResults = TranslationService.translate("noResults", this.language);

    return html`
      <div class="combobox">
        <div class="combobox-trigger" @click=${this.handleToggleOpen}>
          <input
            type="text"
            class="combobox-input"
            placeholder=${displayText}
            .value=${this.searchQuery}
            @input=${this.handleSearchInput}
            @focus=${this.handleInputFocus}
            @blur=${this.handleInputBlur}
            @click=${(e: Event) => e.stopPropagation()}
          />
          ${this.searchQuery
            ? html`
                <button
                  class="combobox-clear"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this.handleClearSearch();
                  }}
                  aria-label=${clearSearch}
                >
                  ×
                </button>
              `
            : ""}
          <span class="combobox-arrow ${this.isOpen ? "open" : ""}">></span>
        </div>
        ${this.isOpen
          ? html`
              <div class="combobox-dropdown">
                ${this.filteredOptions.length > 0
                  ? html`
                      ${this.filteredOptions.map(
                        (option) => html`
                          <div
                            class="combobox-option ${this.selectedValues.includes(
                              option.id,
                            )
                              ? "selected"
                              : ""}"
                            @mousedown=${(e: Event) => {
                              e.preventDefault();
                              this.toggleOption(option.id);
                            }}
                          >
                            <div
                              class="combobox-checkbox ${this.selectedValues.includes(
                                option.id,
                              )
                                ? "checked"
                                : ""}"
                            ></div>
                            <span class="combobox-option-label"
                              >${option.label}</span
                            >
                            ${option.count !== undefined
                              ? html`
                                  <span class="combobox-option-count"
                                    >${option.count}</span
                                  >
                                `
                              : ""}
                          </div>
                        `,
                      )}
                    `
                  : html`<div class="combobox-no-results">${noResults}</div>`}
              </div>
            `
          : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-combobox": Combobox;
  }
}
